import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import RenewalModal from './RenewalModal';

const SubscriptionTable = ({ 
  subscriptions, 
  selectedClients, 
  onSelectClient, 
  onSelectAll, 
  activeTab 
}) => {
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const handleRenewal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRenewalModal(true);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status, days) => {
    if (status === 'ending-soon') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Icon name="Clock" size={12} className="mr-1" />
          {days} days left
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Icon name="AlertCircle" size={12} className="mr-1" />
          {days} days overdue
        </span>
      );
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedClients.length === subscriptions.length && subscriptions.length > 0}
                  onChange={onSelectAll}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                End Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions.map((subscription) => (
              <tr 
                key={subscription.id}
                className={`hover:bg-gray-50 ${
                  activeTab === 'ending-soon' ? 'bg-yellow-50' : 'bg-red-50'
                }`}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(subscription.id)}
                    onChange={() => onSelectClient(subscription.id)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Image
                      src={subscription.avatar}
                      alt={subscription.clientName}
                      className="w-10 h-10 rounded-full mr-4"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.clientName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {subscription.phone}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{subscription.subscriptionType}</div>
                  <div className="text-sm text-gray-500">
                    Last renewed: {formatDate(subscription.lastRenewal)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(subscription.endDate)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(
                    subscription.status, 
                    activeTab === 'ending-soon' ? subscription.daysRemaining : subscription.daysOverdue
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRenewal(subscription)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Icon name="RefreshCw" size={14} className="mr-1" />
                      Renew
                    </button>
                    <button className="text-gray-400 hover:text-gray-500">
                      <Icon name="Phone" size={16} />
                    </button>
                    <button className="text-gray-400 hover:text-gray-500">
                      <Icon name="Mail" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {subscriptions.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab === 'ending-soon' ? 'ending soon' : 'expired'} subscriptions
            </h3>
            <p className="text-gray-500">
              {activeTab === 'ending-soon' ?'All subscriptions are in good standing' :'No expired subscriptions to manage'
              }
            </p>
          </div>
        )}
      </div>

      {/* Renewal Modal */}
      {showRenewalModal && selectedSubscription && (
        <RenewalModal
          subscription={selectedSubscription}
          onClose={() => setShowRenewalModal(false)}
          onRenewal={() => {
            setShowRenewalModal(false);
            setSelectedSubscription(null);
          }}
        />
      )}
    </>
  );
};

export default SubscriptionTable;